import { Check, Plus } from 'lucide-react'
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Progress } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ProjectIcon } from '@/components/project-icon'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import {
  filterProjectGroups,
  shouldAutofocusProjectFilter,
  shouldShowProjectFilter,
  type SwitcherGroup,
} from '@/lib/project-switcher'

export interface SwitcherProjectRow {
  id: string
  name: string
  icon: string | null
  avatarUrl: string | null
}

interface ProjectSwitcherProps {
  groups: SwitcherGroup<SwitcherProjectRow>[]
  currentProjectId?: string
  onSelect: (projectId: string) => void
  /** The button that opens the list; rendered through `asChild`. */
  trigger: ReactElement
  /** Shown to the right of the trigger (collapsed rail). */
  tooltip?: ReactNode
  /**
   * The mobile drawer is a modal Sheet whose scroll lock only lets the sheet itself scroll —
   * the portaled list must own the lock instead (`modal`), or touch-scrolling it is cancelled.
   */
  inDrawer?: boolean
  /** Quota footer + "new project" for organizations the user may create in. */
  createProject?: { count: number; limit: number; onCreate: () => void }
}

/**
 * Organization-grouped project list behind the sidebar trigger. Above
 * `PROJECT_FILTER_THRESHOLD` projects a filter box sits on top (autofocused only with a
 * physical keyboard); the query resets every time the list closes.
 */
export function ProjectSwitcher({
  groups,
  currentProjectId,
  onSelect,
  trigger,
  tooltip,
  inDrawer = false,
  createProject,
}: ProjectSwitcherProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const commandRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  const showFilter = shouldShowProjectFilter(groups)
  const visible = useMemo(() => filterProjectGroups(groups, query), [groups, query])

  useVisualViewportMaxHeight(contentRef, open)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setQuery('')
  }

  const select = (projectId: string) => {
    handleOpenChange(false)
    onSelect(projectId)
  }

  const triggerNode = <PopoverTrigger asChild>{trigger}</PopoverTrigger>

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={inDrawer}>
      {tooltip ? (
        <TooltipProvider delayDuration={500}>
          <Tooltip>
            <TooltipTrigger asChild>{triggerNode}</TooltipTrigger>
            <TooltipContent side="right">{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        triggerNode
      )}
      <PopoverContent
        ref={contentRef}
        align="start"
        className="flex w-56 flex-col overflow-hidden p-0 max-h-(--radix-popover-content-available-height)"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          const autofocus =
            showFilter &&
            shouldAutofocusProjectFilter({
              phone: isMobile,
              coarsePointer: window.matchMedia('(pointer: coarse)').matches,
            })
          // Keyboard users get the box; everyone else gets the list (arrows/Enter still work).
          const target = autofocus ? inputRef.current : commandRef.current
          target?.focus()
        }}
      >
        <Command
          ref={commandRef}
          tabIndex={-1}
          label={t('sidebar.projects')}
          shouldFilter={false}
          loop
          defaultValue={currentProjectId}
          className="min-h-0 outline-hidden"
        >
          {showFilter && (
            <CommandInput
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder={t('sidebar.searchProjects')}
              aria-controls={listId}
              className="h-9"
            />
          )}
          <CommandList id={listId} className="min-h-0 flex-1 max-h-none overscroll-contain p-1">
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
              {t('sidebar.noProjectsMatch')}
            </CommandEmpty>
            {visible.map((group, idx) => (
              <CommandGroup
                key={group.organization.id}
                heading={group.organization.name}
                className={cn('p-0', idx > 0 && 'border-t border-border mt-1 pt-1')}
              >
                {group.projects.map((project) => (
                  <CommandItem
                    key={project.id}
                    value={project.id}
                    onSelect={() => select(project.id)}
                    className={cn(
                      'cursor-pointer',
                      currentProjectId === project.id && 'bg-accent/50'
                    )}
                  >
                    <ProjectIcon
                      name={project.name}
                      icon={project.icon}
                      avatarUrl={project.avatarUrl}
                      size="sm"
                    />
                    <span className="flex-1 truncate">{project.name}</span>
                    {currentProjectId === project.id && <Check className="h-4 w-4 flex-shrink-0" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>

          {createProject && (
            <>
              <div className="border-t border-border" />
              <div className="space-y-1.5 px-3 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t('sidebar.projects')}</span>
                  <span className="text-xs text-muted-foreground">
                    {createProject.count}/{createProject.limit}
                  </span>
                </div>
                <Progress
                  value={(createProject.count / createProject.limit) * 100}
                  className="h-1.5"
                />
              </div>
              <div className="p-1">
                <button
                  type="button"
                  disabled={createProject.count >= createProject.limit}
                  onClick={() => {
                    handleOpenChange(false)
                    createProject.onCreate()
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent disabled:pointer-events-none disabled:opacity-50 [&_svg]:text-muted-foreground"
                >
                  <Plus className="h-4 w-4" />
                  {t('sidebar.newProject')}
                </button>
              </div>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Radix sizes the popover from the visual viewport, but only re-measures on window resize —
 * which iOS does not fire when the on-screen keyboard opens. Clamp the content to the part
 * of the screen the keyboard leaves visible while the list is open.
 */
function useVisualViewportMaxHeight(ref: RefObject<HTMLDivElement | null>, active: boolean) {
  useEffect(() => {
    const vv = window.visualViewport
    if (!active || !vv) return
    const apply = () => {
      const el = ref.current
      if (!el) return
      const keyboardUp = vv.height < window.innerHeight - 1
      if (!keyboardUp) {
        el.style.maxHeight = ''
        return
      }
      const room = vv.offsetTop + vv.height - el.getBoundingClientRect().top - 8
      el.style.maxHeight = `${Math.max(120, Math.floor(room))}px`
    }
    apply()
    vv.addEventListener('resize', apply)
    vv.addEventListener('scroll', apply)
    return () => {
      vv.removeEventListener('resize', apply)
      vv.removeEventListener('scroll', apply)
    }
  }, [ref, active])
}
